import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { SelectField, Spinner } from "@/components/reusable";
import { schemaRequiresReportType } from "@/config/reportType";
import type { PendingPipelineUpload } from "@/lib/pipelineTypes";
import type { TargetSchema } from "@/types";

interface PendingPipelineFilesListProps {
  entries: PendingPipelineUpload[];
  schemas: TargetSchema[];
  isLoadingSchemas: boolean;
  onEntrySchemaChange: (index: number, schemaId: string) => void;
  onAllSchemasChange: (schemaId: string) => void;
  onEntryReportTypeChange: (index: number, reportType: string) => void;
  onRemoveEntry: (index: number) => void;
}

export function PendingPipelineFilesList({
  entries,
  schemas,
  isLoadingSchemas,
  onEntrySchemaChange,
  onAllSchemasChange,
  onEntryReportTypeChange,
  onRemoveEntry,
}: PendingPipelineFilesListProps) {
  const { t } = useTranslation();

  if (entries.length === 0) return null;

  const schemaOptions = schemas.map((schema) => ({ value: schema.id, label: schema.name }));
  const schemaNameById = Object.fromEntries(schemas.map((schema) => [schema.id, schema.name]));
  const firstSchemaId = entries[0]?.schemaId ?? "";
  const uniformSchemaId = entries.every((entry) => entry.schemaId === firstSchemaId)
    ? firstSchemaId
    : "";

  const buildReportTypeOptions = (allowedReportTypes: string[] | undefined) =>
    (allowedReportTypes ?? []).map((reportType) => ({
      value: reportType,
      label: t(`upload.fileType.${reportType}`, {
        defaultValue: reportType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      }),
    }));

  return (
    <div className="flex max-h-full min-h-0 flex-col rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg-primary)]">
      <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_11rem_11rem_2rem] items-center gap-3 border-b border-[var(--color-border-light)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
        <span>File</span>
        <SelectField
          value={uniformSchemaId}
          onValueChange={onAllSchemasChange}
          options={schemaOptions}
          placeholder="Select schema for all"
          disabled={isLoadingSchemas}
          className="normal-case tracking-normal"
        />
        <span>Report type</span>
        <span className="sr-only">Remove</span>
      </div>
      <ul className="min-h-0 flex-1 divide-y divide-[var(--color-border-light)] overflow-y-auto">
        {entries.map((entry, index) => {
          const schemaName = schemaNameById[entry.schemaId] ?? entry.schemaId;
          const needsReportType = schemaRequiresReportType(schemaName);
          const reportTypeOptions = buildReportTypeOptions(entry.allowedReportTypes);

          return (
            <li
              key={`${entry.file.name}-${entry.file.size}-${entry.file.lastModified}`}
              className="grid grid-cols-[minmax(0,1fr)_11rem_11rem_2rem] items-start gap-3 px-3 py-2.5"
            >
              <span className="break-words text-sm leading-snug text-[var(--color-text-primary)]">
                {entry.file.name}
              </span>
              <div className="w-44 shrink-0">
                <SelectField
                  value={entry.schemaId}
                  onValueChange={(schemaId) => onEntrySchemaChange(index, schemaId)}
                  options={schemaOptions}
                  placeholder="Select schema"
                  disabled={isLoadingSchemas}
                />
              </div>
              <div className="relative w-44 shrink-0">
                {needsReportType ? (
                  entry.isClassifying ? (
                    <div className="flex h-10 items-center gap-2 px-2 text-xs text-[var(--color-text-secondary)]">
                      <Spinner size="sm" />
                      <span>{t("upload.fileTypeClassifying")}</span>
                    </div>
                  ) : (
                    <SelectField
                      value={entry.reportType ?? ""}
                      onValueChange={(reportType) => onEntryReportTypeChange(index, reportType)}
                      options={reportTypeOptions}
                      placeholder={t("upload.fileTypePlaceholder")}
                      disabled={isLoadingSchemas || reportTypeOptions.length === 0}
                    />
                  )
                ) : (
                  <span className="flex h-10 items-center px-2 text-sm text-[var(--color-text-tertiary)]">
                    —
                  </span>
                )}
              </div>
              <button
                type="button"
                className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
                onClick={() => onRemoveEntry(index)}
                aria-label={`Remove ${entry.file.name}`}
              >
                <X className="h-4 w-4" strokeWidth={2} aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
